import React, { useState, useMemo } from 'react';
import { X, Check, Loader2, Clock, CheckCircle2, XCircle, FileText, DollarSign, Receipt, CreditCard, ClipboardList, Filter } from 'lucide-react';

// 审批类型配置
const APPROVAL_TYPE_CONFIG = {
  demand_approval: { label: '采购需求审批', icon: ClipboardList, color: 'blue', relatedLabel: '采购需求单', relatedPath: '/demand' },
  contract_approval: { label: '合同审批', icon: FileText, color: 'purple', relatedLabel: '关联订单', relatedPath: '/orders' },
  settlement_approval: { label: '结算审批', icon: DollarSign, color: 'green', relatedLabel: '结算单', relatedPath: '/settlement' },
  invoice_approval: { label: '开票审批', icon: Receipt, color: 'orange', relatedLabel: '发票单', relatedPath: '/invoice' },
  payment_approval: { label: '付款审批', icon: CreditCard, color: 'red', relatedLabel: '付款单', relatedPath: '/payment' },
};

const ApprovalView = ({ approvals = [] }) => {
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedTask, setSelectedTask] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');

  // 根据状态筛选
  const filteredByStatus = useMemo(() => {
    if (activeTab === 'pending') {
      return approvals.filter(t => t.status === 'pending');
    } else if (activeTab === 'processed') {
      return approvals.filter(t => t.status === 'approved' || t.status === 'rejected');
    }
    return approvals;
  }, [approvals, activeTab]);

  // 根据类型筛选
  const filteredTasks = useMemo(() => {
    if (typeFilter === 'all') return filteredByStatus;
    return filteredByStatus.filter(t => t.type === typeFilter);
  }, [filteredByStatus, typeFilter]);

  // 获取类型标签样式
  const getTypeStyle = (type) => {
    const config = APPROVAL_TYPE_CONFIG[type] || { color: 'slate' };
    const colorClasses = {
      blue: 'bg-blue-50 text-blue-600 border-blue-100',
      purple: 'bg-purple-50 text-purple-600 border-purple-100',
      green: 'bg-green-50 text-green-600 border-green-100',
      orange: 'bg-orange-50 text-orange-600 border-orange-100',
      red: 'bg-red-50 text-red-600 border-red-100',
      slate: 'bg-slate-50 text-slate-600 border-slate-100',
    };
    return colorClasses[config.color] || colorClasses.slate;
  };

  // 获取优先级标签样式
  const getPriorityStyle = (priority) => {
    const styles = {
      high: 'bg-red-100 text-red-700',
      medium: 'bg-yellow-100 text-yellow-700',
      low: 'bg-slate-100 text-slate-600',
    };
    return styles[priority] || styles.medium;
  };

  // 审批详情弹窗
  const ApprovalDetailModal = ({ task, onClose }) => {
    if (!task) return null;
    const typeConfig = APPROVAL_TYPE_CONFIG[task.type] || { label: '未知类型', icon: FileText };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] animate-in fade-in duration-200">
        <div className="bg-white rounded-xl shadow-xl w-[750px] max-h-[90vh] overflow-y-auto flex flex-col">
          {/* 头部 */}
          <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50 sticky top-0 z-10">
            <div>
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                {task.title}
                <span className={`px-2 py-0.5 rounded text-xs border ${
                  task.status === 'pending' ? 'bg-orange-50 text-orange-600 border-orange-200' :
                  task.status === 'approved' ? 'bg-green-50 text-green-600 border-green-200' :
                  'bg-red-50 text-red-600 border-red-200'
                }`}>
                  {task.status === 'pending' ? '审批中' : task.status === 'approved' ? '已批准' : '已驳回'}
                </span>
              </h3>
              <p className="text-xs text-slate-500 mt-1">
                单号: {task.id} | 申请人: {task.applicant} | 提交时间: {task.submitTime}
              </p>
            </div>
            <button onClick={onClose}>
              <X size={20} className="text-slate-400 hover:text-slate-600" />
            </button>
          </div>

          {/* 内容区 */}
          <div className="p-6 space-y-6">
            {/* 基本信息 */}
            <div>
              <h4 className="text-sm font-bold text-slate-800 mb-4 border-l-4 border-blue-600 pl-2">审批信息</h4>
              <div className="bg-slate-50 rounded-lg p-4 grid grid-cols-2 gap-y-4 gap-x-8 text-sm">
                <div>
                  <span className="text-slate-500 block mb-1">审批类型</span>
                  <span className={`px-2 py-1 rounded text-xs border ${getTypeStyle(task.type)}`}>
                    {typeConfig.label}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500 block mb-1">优先级</span>
                  <span className={`px-2 py-1 rounded text-xs ${getPriorityStyle(task.priority)}`}>
                    {task.priority === 'high' ? '高' : task.priority === 'medium' ? '中' : '低'}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500 block mb-1">当前节点</span>
                  <span className="font-medium text-slate-800">{task.currentStep}</span>
                </div>
                <div>
                  <span className="text-slate-500 block mb-1">接收日期</span>
                  <span className="font-medium text-slate-800">{task.date}</span>
                </div>
                {task.orderId && (
                  <div>
                    <span className="text-slate-500 block mb-1">关联订单</span>
                    <span className="font-medium text-blue-600">{task.orderId}</span>
                  </div>
                )}
              </div>
            </div>

            {/* 关联单据 */}
            {task.relatedId && (
              <div>
                <h4 className="text-sm font-bold text-slate-800 mb-4 border-l-4 border-purple-600 pl-2">关联单据</h4>
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {React.createElement(typeConfig.icon, { size: 20, className: 'text-purple-600' })}
                    <div>
                      <span className="text-sm text-slate-500">{typeConfig.relatedLabel}</span>
                      <span className="ml-2 font-mono font-bold text-slate-800">{task.relatedId}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      // 这里可以添加跳转逻辑，目前仅提示
                      alert(`即将跳转到${typeConfig.relatedLabel}详情页：${task.relatedId}`);
                    }}
                    className="px-4 py-2 bg-white border border-purple-200 text-purple-600 rounded-lg text-sm font-medium hover:bg-purple-100 hover:border-purple-300 transition-colors flex items-center gap-1"
                  >
                    查看详情
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                      <polyline points="15 3 21 3 21 9"/>
                      <line x1="10" y1="14" x2="21" y2="3"/>
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* 审批流程 */}
            {task.flow && (
              <div>
                <h4 className="text-sm font-bold text-slate-800 mb-4 border-l-4 border-blue-600 pl-2">审批流程进度</h4>
                <div className="space-y-0">
                  {task.flow.map((node, index) => (
                    <div key={index} className="flex gap-4 relative pb-8 last:pb-0">
                      {index !== task.flow.length - 1 && (
                        <div className={`absolute left-[15px] top-8 bottom-0 w-0.5 ${
                          node.status === 'approved' ? 'bg-green-300' : 'bg-slate-200'
                        }`}></div>
                      )}
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 z-10 ${
                        node.status === 'approved' ? 'bg-green-100 text-green-600' :
                        node.status === 'pending' ? 'bg-blue-100 text-blue-600 ring-4 ring-blue-50' :
                        node.status === 'rejected' ? 'bg-red-100 text-red-600' :
                        'bg-slate-100 text-slate-400'
                      }`}>
                        {node.status === 'approved' ? <Check size={16} /> :
                         node.status === 'rejected' ? <XCircle size={16} /> :
                         node.status === 'pending' ? <Loader2 size={16} className="animate-spin" /> :
                         <Clock size={16} />}
                      </div>
                      <div className="flex-1 pt-1">
                        <div className="flex justify-between">
                          <span className={`text-sm font-bold ${
                            node.status === 'pending' ? 'text-blue-600' :
                            node.status === 'approved' ? 'text-green-600' :
                            node.status === 'rejected' ? 'text-red-600' :
                            'text-slate-400'
                          }`}>
                            {node.step}
                          </span>
                          <span className="text-xs text-slate-400">{node.time || '-'}</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">
                          处理人: {node.approver} ({node.approverRole})
                        </p>
                        {node.remark && (
                          <p className="text-xs text-slate-400 mt-1 italic">"{node.remark}"</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* 底部按钮 */}
          <div className="p-4 border-t border-slate-100 flex justify-end gap-3 bg-white sticky bottom-0">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-200"
            >
              关闭
            </button>
            {task.status === 'pending' && (
              <>
                <button
                  onClick={() => onClose()}
                  className="px-6 py-2 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100"
                >
                  驳回
                </button>
                <button
                  onClick={() => onClose()}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                >
                  同意
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Tab 切换 */}
      <div className="flex gap-6 border-b border-slate-200">
        {[
          { id: 'pending', label: '待办审批', count: approvals.filter(t => t.status === 'pending').length },
          { id: 'processed', label: '已办审批', count: approvals.filter(t => t.status !== 'pending').length },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`pb-3 text-sm font-medium relative flex items-center gap-2 ${
              activeTab === tab.id ? 'text-blue-600' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                activeTab === tab.id ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500'
              }`}>
                {tab.count}
              </span>
            )}
            {activeTab === tab.id && (
              <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 rounded-t-full"></div>
            )}
          </button>
        ))}
      </div>

      {/* 类型筛选 */}
      <div className="flex items-center gap-2">
        <Filter size={16} className="text-slate-400" />
        <span className="text-sm text-slate-500">类型筛选:</span>
        <div className="flex gap-2">
          <button
            onClick={() => setTypeFilter('all')}
            className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
              typeFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            全部
          </button>
          {Object.entries(APPROVAL_TYPE_CONFIG).map(([type, config]) => (
            <button
              key={type}
              onClick={() => setTypeFilter(type)}
              className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors flex items-center gap-1 ${
                typeFilter === type ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <config.icon size={12} />
              {config.label}
            </button>
          ))}
        </div>
      </div>

      {/* 审批列表 */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-slate-500 border-b border-slate-100">
            <tr>
              <th className="p-4 pl-6">审批标题</th>
              <th className="p-4">类型</th>
              <th className="p-4">申请人</th>
              <th className="p-4">提交时间</th>
              <th className="p-4">优先级</th>
              <th className="p-4">当前节点/状态</th>
              <th className="p-4 text-right pr-6">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredTasks.length === 0 ? (
              <tr>
                <td colSpan={7} className="p-8 text-center text-slate-400">
                  暂无{activeTab === 'pending' ? '待办' : '已办'}审批
                </td>
              </tr>
            ) : (
              filteredTasks.map(task => (
                <tr key={task.id} className="hover:bg-slate-50">
                  <td className="p-4 pl-6">
                    <div className="font-medium text-slate-800">{task.title}</div>
                    <div className="text-xs text-slate-400 font-mono mt-0.5">{task.id}</div>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded text-xs border ${getTypeStyle(task.type)}`}>
                      {APPROVAL_TYPE_CONFIG[task.type]?.label || '未知类型'}
                    </span>
                  </td>
                  <td className="p-4 text-slate-600">{task.applicant}</td>
                  <td className="p-4 text-slate-600">{task.submitTime}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded text-xs ${getPriorityStyle(task.priority)}`}>
                      {task.priority === 'high' ? '高' : task.priority === 'medium' ? '中' : '低'}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className={`text-xs flex items-center gap-1 ${
                      task.status === 'pending' ? 'text-orange-600' :
                      task.status === 'approved' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {task.status === 'pending' ? <Clock size={12}/> :
                       task.status === 'approved' ? <CheckCircle2 size={12}/> : <XCircle size={12}/>}
                      {task.status === 'pending' ? task.currentStep :
                       (task.status === 'approved' ? '已批准' : '已驳回')}
                    </span>
                  </td>
                  <td className="p-4 text-right pr-6">
                    <button
                      onClick={() => setSelectedTask(task)}
                      className="text-blue-600 hover:text-blue-800 font-medium text-xs"
                    >
                      查看
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* 详情弹窗 */}
      {selectedTask && <ApprovalDetailModal task={selectedTask} onClose={() => setSelectedTask(null)} />}
    </div>
  );
};

export default ApprovalView;
